/**
 * Resolve `createdBy` values on test configurations / runs to a display name.
 *
 * `createdBy` is a Firebase UID for people, or a tagged string for machines
 * (`apikey:<id>`, `qa-auto:…`, `system`, `campaign…`). People are looked up in
 * the org's members subcollection (any member may list it). Super admins who
 * aren't members of the org — e.g. support running a test for a customer —
 * aren't there, so super-admin viewers fall back to `users/{uid}` (readable
 * only by super admins); everyone else sees "Unknown user" for them.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';

/** Label for non-person `createdBy` values, or null if it looks like a UID. */
function machineLabel(createdBy: string): string | null {
  if (createdBy.startsWith('apikey:')) return 'API key';
  if (createdBy.startsWith('qa-auto:')) return 'QA automation';
  if (createdBy === 'system' || createdBy === 'system_seed') return 'System';
  if (createdBy.startsWith('campaign')) return 'Campaign';
  return null;
}

export function useUserNames(
  orgId: string | undefined,
  createdByValues: Array<string | undefined | null>,
  isSuperAdmin: boolean,
) {
  const [names, setNames] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  // Stable key so the effect only re-runs when the set of UIDs changes.
  const uidKey = useMemo(
    () => Array.from(new Set(createdByValues.filter((v): v is string => !!v && !machineLabel(v)))).sort().join(','),
    [createdByValues],
  );

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      try {
        const snap = await getDocs(collection(db, 'organizations', orgId, 'members'));
        snap.forEach((d) => {
          const data = d.data();
          const name = data.displayName || data.email;
          if (name) map[d.id] = name;
        });
      } catch (e) {
        console.warn('Failed to load org members for user names:', e);
      }
      if (isSuperAdmin && uidKey) {
        const missing = uidKey.split(',').filter((uid) => !map[uid]);
        await Promise.all(missing.map(async (uid) => {
          try {
            const u = await getDoc(doc(db, 'users', uid));
            const data = u.data();
            const name = data?.displayName || data?.email;
            if (name) map[uid] = name;
          } catch {
            // Deleted user or no access — leave as unknown.
          }
        }));
      }
      if (!cancelled) {
        setNames(map);
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, uidKey, isSuperAdmin]);

  return useMemo(
    () => (createdBy: string | undefined | null): string => {
      if (!createdBy) return '—';
      return machineLabel(createdBy) ?? names[createdBy] ?? (loaded ? 'Unknown user' : '…');
    },
    [names, loaded],
  );
}
